<template>
  <div style="position: static;" :id="uniqueIdLocal">
    <popper
      :boundingEleID="parentEleId"
      :customAttribs="{ show: listActive }"
      :disable="false"
      :hostEleID="`${uniqueIdLocal}-timepicker-display`"
      :popoverID="`${uniqueIdLocal}-popover`"
      :tooltipMode="false"
      :warning="false"
      placement="bottom-start"
      @emitError="$emit('emitError', err)">
      <slot>
        <div
          :id="`${uniqueIdLocal}-timepicker-display`"
          class="time-picker-container">
          <div
            class="time-picker-preview"
            :class="{
              disabled: disabled,
              'disabled-input': disabled,
              'time-picker-preview-focus': listActive,
            }">
            <span class="clock">
              <font-awesome-icon :icon="faIcons.faClock" />
            </span>
            <input
              aria-label="Time"
              :ref="`${uniqueId}-time-input-text-box`"
              type="text"
              onfocus="this.select();"
              :disabled="disabled || null"
              :value="time"
              :id="inputId"
              :style="{
                cursor: disabled ? 'not-allowed' : 'inherit',
              }"
              class="time-picker"
              @input="handleUserEntry"
              @focus="listShow"
              @blur="() => {
                selectTimeHighlighted();
                listHide();
              }"
              @keydown.tab="(event) => {
                selectTimeHighlighted();
                // If shift then let browser handle
                // moving back but otherwise move to
                // the next time picker.
                if (!event.shiftKey) {
                  nextFocus(event);
                }
              }"
              @keydown.down="selectNext"
              @keydown.left="selectLeft"
              @keydown.right="selectRight"
              @keydown.up="selectPrev"
              @keydown.enter="selectTimeHighlighted" />
          </div>
        </div>
      </slot>
      <template #popover>
        <div
          :id="`${uniqueIdLocal}-popover`"
          class="menu-position-absolute"
          style="display: table;"
          :style="{
            'min-width': (noKeyPressesOrSelectionsMade &&
              defaultTimes.length > 0) ? '225px' : '125px',
          }">
          <ul
            class="menu alignment-list-item"
            v-show="timeOptionsAll.length > 0 && showListInternal"
            :id="elementIds.times">
            <li
              class="menu-item-header"
              style="position: relative;"
              v-if="noKeyPressesOrSelectionsMade && defaultTimes.length > 0">
              <em>Recently selected times</em>
              <button class="clear" @mousedown.prevent="listClear">
                <font-awesome-icon :icon="faIcons.faTimesSolid" /> Clear
              </button>
            </li>
            <div
              v-for="(item, idx) in timeOptionsAll"
              style="display: table-row;"
              :key="`times-iteration-${item}-${idx}`">
              <li
                class="menu-item"
                :id="elementIds.timeInTimes(item)"
                :class="{
                  'disabled-item': !lodash.includes(timeOptions, item),
                  'menu-item-emphasis': timeStrIs30MinInterval(item) &&
                    !(noKeyPressesOrSelectionsMade &&
                      defaultTimes.length > 0) &&
                    keyEntry.length >= 1 &&
                    timeOptionsAll.length > 1 &&
                    lodash.includes(timeOptions, item),
                  'menu-item-emphasis-disabled': timeStrIs30MinInterval(item) &&
                    !(noKeyPressesOrSelectionsMade &&
                      defaultTimes.length > 0) &&
                    keyEntry.length >= 1 &&
                    timeOptionsAll.length > 1 &&
                    !lodash.includes(timeOptions, item),
                  selected: cleanStr(item) === cleanStr(highlightingCursor),
                }"
                role="option"
                :aria-selected="cleanStr(item) === cleanStr(highlightingCursor)"
                tabindex="0"
                :key="`timepicker-${uniqueIdLocal}-idx-${idx}`"
                @keydown.enter="() => { selectTime(item); listHide(); }"
                @mousedown.prevent
                @click="() => { selectTime(item); listHide(); }">
                <div class="tile tile-centered">
                  <div class="tile-content">
                    {{ item }}
                  </div>
                </div>
              </li>
              <li
                class="menu-item menu-item-inline"
                v-if="timeOptionsAll.length ===
                  ambigTimeInput12HOptionsAll.length"
                :id="elementIds.timeInTimesAmbig(
                  ambigTimeInput12HOptionsAll[idx])"
                :class="{
                  'disabled-item': !lodash.includes(
                    ambigTimeInput12HOptions, ambigTimeInput12HOptionsAll[idx]),
                  'menu-item-emphasis': timeStrIs30MinInterval(item) &&
                    !(noKeyPressesOrSelectionsMade &&
                      defaultTimes.length > 0) &&
                    keyEntry.length >= 1 &&
                    ambigTimeInput12HOptionsAll.length > 1 &&
                    lodash.includes(
                      ambigTimeInput12HOptions,
                      ambigTimeInput12HOptionsAll[idx]),
                  'menu-item-emphasis-disabled': timeStrIs30MinInterval(item) &&
                    !(noKeyPressesOrSelectionsMade &&
                      defaultTimes.length > 0) &&
                    keyEntry.length >= 1 &&
                    ambigTimeInput12HOptionsAll.length > 1 &&
                    !lodash.includes(
                      ambigTimeInput12HOptions,
                      ambigTimeInput12HOptionsAll[idx]),
                  selected: cleanStr(ambigTimeInput12HOptionsAll[idx]) ===
                    cleanStr(highlightingCursor),
                }"
                role="option"
                :aria-selected="cleanStr(ambigTimeInput12HOptionsAll[idx]) ===
                    cleanStr(highlightingCursor)"
                tabindex="0"
                :key="`timepicker-${uniqueIdLocal}-ambig-idx-${idx}`"
                @mousedown.prevent
                @keydown.enter="() => {
                  selectTime(ambigTimeInput12HOptionsAll[idx]);
                  listHide();
                }"
                @click="() => {
                  selectTime(ambigTimeInput12HOptionsAll[idx]);
                  listHide();
                }
                  ">
                <div class="tile tile-centered">
                  <div class="tile-content">
                    {{ ambigTimeInput12HOptionsAll[idx] }}
                  </div>
                </div>
              </li>
            </div>
          </ul>
        </div>
      </template>
    </popper>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';

import {
  CleanStr,
  HoursAs15MinuteIntervals,
  HoursAs30MinuteIntervals,
  HoursAs60MinuteIntervals,
  MomentFormat12H,
  MomentFormat24H,
  RegExp12HTimeFormat,
  RegExp24HTimeFormat,
  TimeIntervalsGenerator,
  TimeStrIs30MinInterval,
  TwelveHourTimeLocales,
} from 'assets/js/widgets/timepicker';

import Popper from 'components/Widgets/Popper/Popper';

export default {
  name: 'time-picker',

  beforeUnmount() {
    if (this.uniqueIdLocal && this.dispenseGlobalProp) {
      this.dispenseGlobalProp.unsubscribe(this.uniqueIdLocal);
    }
  },

  components: {
    Popper,
  },

  computed: {
    // ambigTimeInput12HOptions pulls the main list of PM 12-hour time
    // options and then wraps it in the disabledTime function to filter
    // out times we don't want to show.
    ambigTimeInput12HOptions() {
      return _.reject(this.ambigTimeInput12HOptionsAll, (opt) =>
        this.disabledTime(
          moment(opt, this.twentyFourHour ? MomentFormat24H :
            MomentFormat12H)));
    },

    // ambigTimeInput12HOptionsAll is an extra list of AM/PM time
    // options for when the time value is ambiguous because AM/PM is not
    // entered.
    ambigTimeInput12HOptionsAll() {
      if (this.twentyFourHour) return [];

      // No reason to return anything here if we're just displaying the
      // recently used times from the user.
      if (this.noKeyPressesOrSelectionsMade && this.defaultTimes.length > 0) {
        return [];
      }

      let times = TimeIntervalsGenerator(this.keyEntry, this.twentyFourHour,
        true, this.minuteIntervalRestrictionDisplay);

      // Filter by restricted minutes.
      times = this.filterTimeIntervalsByRestrictedMinutes(times);

      // We have one entry and it's the same as what we'd spit out because
      // it's a valid time string but not an option on the menu.
      if (_.isEqual(this.timeOptionsAll, times)) return [];

      return times;
    },

    // timeOptions pulls the main list of time options and then wraps it in
    // the disabledTime function to filter out times we don't want to show.
    timeOptions() {
      return _.reject(this.timeOptionsAll, (opt) =>
        this.disabledTime(
          moment(opt, this.twentyFourHour ? MomentFormat24H :
            MomentFormat12H)));
    },

    // The list of time options to appear in the dropdown based on the
    // default times and user input.
    timeOptionsAll() {
      if (this.noKeyPressesOrSelectionsMade ||
          (!this.noKeyPressesOrSelectionsMade && this.keyEntry === '')) {
        // Only display the default options if the user has not yet made a
        // selections.
        if (this.defaultTimes.length > 0 &&
            this.noKeyPressesOrSelectionsMade) {
          return _.map(this.defaultTimes, (time) =>
            moment(time, 'HH:mm')
              .format(this.twentyFourHour ? MomentFormat24H :
                MomentFormat12H));
        }

        switch (this.minuteIntervalRestrictionDisplay) {
          case 15:
            return HoursAs15MinuteIntervals(this.twentyFourHour,
              // Need to double this to allow for more options.
              (this.twentyFourHour ? 24 : 12), // Duration
              0, // Start hour
              0); // Start minute

          case 60:
            return HoursAs60MinuteIntervals(this.twentyFourHour,
              (this.twentyFourHour ? 24 : 12), // Duration
              0, // Start hour
              0); // Start minute

          case 30:
          default:
            return HoursAs30MinuteIntervals(this.twentyFourHour,
              (this.twentyFourHour ? 24 : 12), // Duration
              0, // Start hour
              0); // Start minute
        }
      }

      if (this.keyEntry !== '') {
        let intervals = TimeIntervalsGenerator(
          this.keyEntry, this.twentyFourHour, null,
          this.minuteIntervalRestriction);
        // Filter by restricted minutes.
        intervals = this.filterTimeIntervalsByRestrictedMinutes(intervals);
        return intervals;
      }

      return [];
    },

    // twentyFourHour determines whether or not the display for time should be
    // 24 hour or 12 hour+AM/PM.
    twentyFourHour() {
      if (_.isString(this.mode) && this.mode.toLowerCase() === '12h') {
        return false;
      }
      if (_.isString(this.mode) && this.mode.toLowerCase() === '24h') {
        return true;
      }

      let lang;
      if (!_.isUndefined(navigator.languages)) {
        lang = navigator.languages[0];
      } else {
        lang = navigator.language;
      }

      if (!lang) return false;

      if (_.includes(TwelveHourTimeLocales, lang.toLowerCase())) {
        return false;
      }
      return true;
    },
  },

  created() {
    if (this.dispenseGlobalProp) {
      this.uniqueIdLocal = this.name;
      if (this.uniqueId) {
        this.uniqueIdLocal = this.uniqueId;
      }

      this.dispenseGlobalProp.subscribe(this.uniqueIdLocal, async (val) => {
        switch (val.name) {
          case 'hourset': {
            await this.handleParentHour(val.value);
            break;
          }
          case 'minuteset': {
            await this.handleParentMinute(val.value);
            break;
          }
          default: {
            throw Error('unknown message from parent component: ' +
                `${JSON.stringify(val)}`);
          }
        }
      });
    }

    this.hour = this.defaultHour;
    this.minute = this.defaultMinute;
    this.setTimeString();
  },

  data() {
    return {
      cleanStr: CleanStr,
      elementIds: {
        timeInTimes: (val) => `${this.uniqueId}-times-val-${CleanStr(val)}`,
        timeInTimesAmbig: (val) =>
          `${this.uniqueId}-times-val-ambig-${CleanStr(val)}`,
        times: `${this.uniqueId}-times-ul`,
      },
      highlightingCursor: '',
      hour: 0,
      keyEntry: '',
      listActive: false,
      minute: 0,

      // Variable that tracks whether or not the user has interacted with
      // the time picker beyond clicking it. If this is true, we just display
      // the common list of times as computed in the backend or, if they're
      // empty, the list of times from the default value in 5 minute
      // intervals.
      noKeyPressesOrSelectionsMade: true,

      // Used to v-show the list after it's been injected into the DOM.
      // Needed to handle internal scrolling of the DOM element.
      showListInternal: false,
      timesEle: undefined,
      time: '',
      timeCached: '',
      timeStrIs30MinInterval: TimeStrIs30MinInterval,
      uniqueIdLocal: '',
    };
  },

  methods: {
    // fetchCursorHighlightingElement fetchs the list element that should
    // currently be highlighted.
    fetchCursorHighlightingElement() {
      let highlightedEle = document.getElementById(
        this.elementIds.timeInTimes(this.highlightingCursor));
      if (_.isUndefined(highlightedEle) || _.isNull(highlightedEle)) {
        highlightedEle = document.getElementById(
          this.elementIds.timeInTimesAmbig(this.highlightingCursor));
      }
      if (_.isUndefined(highlightedEle) || _.isNull(highlightedEle)) {
        highlightedEle = document.getElementById(
          this.elementIds.timeInTimes(this.time));
      }
      if (_.isUndefined(highlightedEle) || _.isNull(highlightedEle)) {
        highlightedEle = document.getElementById(
          this.elementIds.timeInTimesAmbig(this.time));
      }
      return highlightedEle;
    },

    filterTimeIntervalsByRestrictedMinutes(intervals) {
      let filteredIntervals = intervals;
      if (this.minuteIntervalRestriction
        && this.minuteIntervalRestriction >= 5) {
        // Filter out restricted minutes.
        filteredIntervals = _.filter(intervals, (interval) => {
          // Parse out the minutes.  Assumes times are always in the form
          // HH:MM AP or HH:MM.
          const minutes = parseInt(interval.split(':')[1].split(' ')[0], 10);
          return minutes % this.minuteIntervalRestriction === 0;
        });
      }
      return filteredIntervals;
    },

    // handleParentHour handles an incoming change to the hour from a parent
    // component.
    async handleParentHour(hour) {
      this.hour = hour;
    },

    // handleParentMinute handles an incoming change to the minute from a
    // parent component.
    async handleParentMinute(minute) {
      this.minute = minute;
    },

    // handleUserEntry handles keypress events from the input box.
    async handleUserEntry(event) {
      // Deal with IE, which doesn't have 'target'.
      const target = event.target || event.srcElement;
      this.keyEntry = target.value;
      this.time = target.value;
      this.noKeyPressesOrSelectionsMade = false;

      if (target.value) {
        // Wait for the new computed values.
        await this.$nextTick();

        // Set the new highlighted item and scroll to it.
        this.highlightingCursor = this.timeOptionsAll[0];
        const highlightedEle = this.fetchCursorHighlightingElement();
        if (!_.isUndefined(highlightedEle) && !_.isNull(highlightedEle) &&
            !_.isUndefined(this.timesEle) && !_.isNull(this.timesEle)) {
          const bounds = highlightedEle.getBoundingClientRect();
          this.timesEle.scrollTop = bounds.top -
              this.timesEle.getBoundingClientRect().top;
        }
      }
    },

    // listClear clears the list of prepopulated times and shows the user
    // times they can select.
    listClear() {
      this.noKeyPressesOrSelectionsMade = false;
    },

    // listShow shows the list of items, adds a scroll listener, and
    // automatically scrolls to the selected element (if any).
    async listShow() {
      if (this.disabled) return;

      // Cache the time here in case the user complete deletes the time before
      // blurring. Then we can restore it there.
      if (this.time) this.timeCached = this.time;

      // Attempt to set the highlighting cursor string. This doesn't always
      // work, but if it fails fetchCursorHighlightingElement should still
      // be able to fetch the element to scroll to below.
      const timeCurrentStr = _.find(
        _.concat(this.timeOptionsAll, this.ambigTimeInput12HOptionsAll),
        (t) => CleanStr(t) === CleanStr(this.time));
      if (timeCurrentStr) {
        this.highlightingCursor = timeCurrentStr;
      }
      this.listActive = true;
      this.showListInternal = false;

      // Poll until popper has injected the DOM element, then add a scroll
      // watcher to it.
      this.timeEle = undefined;
      const tries = 100;
      const sleepTime = 10;
      for (let i = 0; i < tries; i += 1) {
        this.timesEle = document.getElementById(this.elementIds.times);
        if (!_.isUndefined(this.timesEle) && !_.isNull(this.timesEle)) {
          this.timesEle.addEventListener(
            'scroll',
            this.scrollHandlerTimesList,
          );
          this.timesEle.style.visibility = 'hidden';
          break;
        }
        await this.sleep(sleepTime);
      }

      this.showListInternal = true;

      // Determine if we need to scroll to a highlighted element or not. We
      // don't need to scroll to the element if it's not in the list.
      const elementNeedsScrolling = Boolean(_.find(this.timeOptionsAll,
        (t) => CleanStr(t) === CleanStr(this.time))) ||
          Boolean(_.find(this.ambigTimeInput12HOptionsAll,
            (t) => CleanStr(t) === CleanStr(this.time)));
      if (elementNeedsScrolling) {
        // Find the highlighted element. Poll until we can see it and scroll
        // down.
        for (let i = 0; i < tries; i += 1) {
          const highlightedEle = this.fetchCursorHighlightingElement();
          if (!_.isUndefined(highlightedEle) && !_.isNull(highlightedEle)) {
            const bounds = highlightedEle.getBoundingClientRect();

            // The element is rendered in the DOM, so scroll down. We need to
            // calculate the offset from the times ul versus the li, compared
            // to the top of the window.
            if (bounds.width !== 0 && bounds.height !== 0) {
              this.timesEle.scrollTop = bounds.top -
                  this.timesEle.getBoundingClientRect().top;
              break;
            }
          }

          await this.sleep(sleepTime);
        }
      } else {
        this.timesEle.scrollTop = 0;
      }

      // Hack to try to not show the timepicker menu until after it has
      // scrolled.
      await this.sleep(0);
      if (this.timesEle) {
        this.timesEle.style.visibility = 'visible';
      }
    },

    // listHide hides the list of items and attempts to set the time
    // if there is any user input.
    listHide() {
      if (!this.time && this.keyEntry !== '' &&
          this.timeOptionsAll.length > 0) {
        this.selectTime(this.timeOptionsAll[0]);
      }

      // Make sure the time string is formatted correctly. If it's not,
      // restore the cached time.
      let rexp = RegExp24HTimeFormat;
      if (!this.twentyFourHour) rexp = RegExp12HTimeFormat;
      if ((this.timeCached && !this.time) ||
          (this.timeCached && !rexp.test(this.time))) {
        this.time = this.timeCached;
        this.highlightingCursor = this.time;
        this.keyEntry = '';
      }

      document.getElementById(this.elementIds.times)?.removeEventListener(
        'scroll',
        this.scrollToSelection,
      );
      this.timesEle = null;
      this.listActive = false;
    },

    // Sets focus to the next DOM element if we have been given an ID.
    nextFocus(event) {
      // Find all the .time-picker elements.
      const timePickers = document.getElementsByClassName('time-picker');
      let curIdx = 0;
      // First find which index we are currently in.
      _.forEach(timePickers, (tp, idx) => {
        if (tp.id === this.inputId) curIdx = idx;
      });
      // Even indexes are starts that could tab to ends.
      if (curIdx % 2 === 0) {
        _.forEach(timePickers, (tp, idx) => {
          if (idx === curIdx + 1) {
            tp.focus();
            tp.select();
            event.preventDefault();
          }
        });
      }
    },

    // scrollHandlerTimesList is the scroll handler for the list. It is
    // currently unused.
    scrollHandlerTimesList() {},

    // selectTimeHighlighted sets the currently highlighted time cursor to
    // be the actual time.
    selectTimeHighlighted() {
      // Abort before a blur event.
      if (!_.includes(
        _.concat(this.timeOptions, this.ambigTimeInput12HOptions),
        this.highlightingCursor,
      )) {
        // Revert to the cached time.
        this.time = this.timeCached;
        return;
      }
      this.selectTime(this.highlightingCursor);
      if (_.get(this.$refs, `${this.uniqueId}-time-input-text-box`)) {
        this.$refs[`${this.uniqueId}-time-input-text-box`].blur();
      }
    },

    // selectNext selects the next item on the list.
    selectNext() {
      if (this.timeOptionsAll.length > 0) {
        // 24 hour or 12 hour AM.
        const timeCurrentStrIdx = _.findIndex(this.timeOptionsAll, (t) =>
          CleanStr(t) === CleanStr(this.highlightingCursor));
        if (!(timeCurrentStrIdx >= (this.timeOptionsAll.length - 1)) &&
            timeCurrentStrIdx !== -1) {
          this.highlightingCursor = this.timeOptionsAll[
            timeCurrentStrIdx + 1];
        }

        // 12 hour PM.
        const timeCurrentStrAmbigIdx = _.findIndex(
          this.ambigTimeInput12HOptionsAll,
          (t) => CleanStr(t) === CleanStr(this.highlightingCursor));
        if (!(timeCurrentStrAmbigIdx >=
              (this.ambigTimeInput12HOptionsAll.length - 1)) &&
            timeCurrentStrAmbigIdx !== -1) {
          this.highlightingCursor =
              this.ambigTimeInput12HOptionsAll[timeCurrentStrAmbigIdx + 1];
        }
      }
    },

    // selectLeft selects the item on the left side of the list if this
    // is in 12 hour time and there are two options.
    selectLeft() {
      if (this.timeOptionsAll.length > 0 && this.timeOptionsAll.length ===
          this.ambigTimeInput12HOptionsAll.length) {
        const timeCurrentStrIdx = _.findIndex(
          this.ambigTimeInput12HOptionsAll,
          (t) => CleanStr(t) === CleanStr(this.highlightingCursor));
        if (timeCurrentStrIdx === -1) return;
        this.highlightingCursor = this.timeOptionsAll[timeCurrentStrIdx];
      }
    },

    // selectLeft selects the item on the right side of the list if this
    // is in 12 hour time and there are two options.
    selectRight() {
      if (this.timeOptionsAll.length > 0 && this.timeOptionsAll.length ===
          this.ambigTimeInput12HOptionsAll.length) {
        const timeCurrentStrIdx = _.findIndex(this.timeOptionsAll,
          (t) => CleanStr(t) === CleanStr(this.highlightingCursor));
        if (timeCurrentStrIdx === -1) return;
        this.highlightingCursor =
            this.ambigTimeInput12HOptionsAll[timeCurrentStrIdx];
      }
    },

    // selectPrev selects the previous item on the list.
    selectPrev() {
      if (this.timeOptionsAll.length > 0) {
        // 24 hour or 12 hour AM.
        const timeCurrentStrIdx = _.findIndex(this.timeOptionsAll, (t) =>
          CleanStr(t) === CleanStr(this.highlightingCursor));
        if (!(timeCurrentStrIdx <= 0)) {
          this.highlightingCursor = this.timeOptionsAll[
            timeCurrentStrIdx - 1];
        }

        // 12 hour PM.
        const timeCurrentStrAmbigIdx = _.findIndex(
          this.ambigTimeInput12HOptionsAll,
          (t) => CleanStr(t) === CleanStr(this.highlightingCursor));
        if (!(timeCurrentStrAmbigIdx <= 0)) {
          this.highlightingCursor =
              this.ambigTimeInput12HOptionsAll[timeCurrentStrAmbigIdx - 1];
        }
      }
    },

    // selectTime parses a selected time and sets the local variables to be
    // that time.
    selectTime(timeStr) {
      if (!_.includes(
        _.concat(this.timeOptions, this.ambigTimeInput12HOptions),
        timeStr,
      )) {
        return;
      }
      const time = moment(timeStr, this.twentyFourHour ? MomentFormat24H :
        MomentFormat12H);
      this.hour = time.hour();
      this.minute = time.minute();
      this.setTimeString();
      this.noKeyPressesOrSelectionsMade = false;

      this.$emit('emitTimeObj', {
        hour: this.hour,
        minute: this.minute,
      });
      this.timeCached = this.time;
    },

    // selectTop selects the top item of the list.
    selectTop() {
      if (this.timeOptionsAll.length > 0) {
        this.highlightingCursor = this.timeOptionsAll[0];
      }
    },

    // setTimeString sets the time string to display to the user in the
    // input box.
    setTimeString() {
      this.time = moment(`${this.hour}:${this.minute}`, 'H:m')
        .format(this.twentyFourHour ? MomentFormat24H : MomentFormat12H);
      this.highlightingCursor = this.time;
    },
  },

  mounted() {},

  emits: [
    'emitError',
    'emitTimeObj',
  ],

  props: {
    // Arrays
    defaultTimes: {
      type: Array,
      required: false,
      default: () => [],
    },

    // Booleans
    disabled: {
      type: Boolean,
      required: true,
    },
    // When true, pressing Tab on a time picker jumps to the next time picker
    // in the DOM
    focusNextTimeOnTab: {
      type: Boolean,
      default: false,
    },

    // Functions
    disabledTime: {
      type: Function,
      default: () => false,
    },

    // Numbers
    defaultHour: {
      type: Number,
      required: false,
      default: 10,
    },
    defaultMinute: {
      type: Number,
      required: false,
      default: 0,
    },
    minuteIntervalRestriction: {
      type: Number,
    },
    minuteIntervalRestrictionDisplay: {
      type: Number,
    },

    // Strings
    mode: {
      type: String,
      required: false,
    },
    nextTimePickerFocusId: {
      type: String,
      required: false,
    },
    parentEleId: {
      type: String,
      required: true,
    },
    nextTimeTabId: {
      type: String,
      required: false,
    },
    inputId: {
      type: String,
      required: false,
    },
    uniqueId: {
      type: String,
      required: false,
    },
  },

  watch: {
    hour: {
      handler: function handle() {
        this.setTimeString();
      },
    },
    minute: {
      handler: function handle() {
        this.setTimeString();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~assets/css/colors";
  @import "~assets/css/datetimepicker";

  .disabled-input {
    background-color: #fff !important;
    border-radius: 4px !important;
    border: 1px solid #bfcbd9 !important;
    color: rgb(84, 84, 84) !important;
    cursor: not-allowed;
  }

  .dark-mode {
    .disabled-input {
      background-color: $dmbg !important;
    }
  }

  .menu {
    background: #fff;
    border-style: solid;
    border-color: #e4e7ed;
    border-width: 1px;
    list-style: none;
    margin: 0;
    margin-top: -5px;
    padding-right: 1rem;
    padding-left: 1rem;
    padding-top: 0;
    padding-bottom: 0;
    box-shadow: 0 0;
    z-index: 100;
    min-width: inherit;
    max-height: 340px;
    overflow-y: scroll;
  }
  .dark-mode {
    .menu {
      background: $dmbg;
    }
  }

  .menu-position-absolute {
    position: absolute;
    z-index: 3;
  }

  .menu-extra-am-pm {
    min-width: inherit;
  }

  .menu-item {
    cursor: pointer;
    margin-top: 0;
    padding: 0.5rem 1rem;
    display: table-cell;

    /* Padding in Spectre comes from the <a> tag in menu items which we
      don't use. */
    .tile {
      padding: 0.6rem 0.8rem;
      margin: 0 -0.8rem;
      display: block;
    }

    strong {
      background-color: rgb(255,255,200);
    }
    &.selected {
      background-color: var(--primary-color-lighten-70-pale,
        $primarycolorlighten70pale) !important;
    }
    &:hover {
      background-color: var(--primary-color-lighten-70,
        $primarycolorlighten70) !important;
    }
  }

  .dark-mode {
    .menu-item {
      &.selected, &:hover {
        background-color: #444444 !important;
      }
    }
  }

  .menu-item-emphasis {
    text-shadow: -0.01ex 0 var(--primary-color, #1e394c),
      0.01ex 0 var(--primary-color, #1e394c);
  }

  .menu-item-emphasis-disabled {
    text-shadow: -0.01ex 0 #ccc, 0.01ex 0 #ccc;
  }

  .menu-item-header {
    margin-top: 0;
    padding: 0.25rem 0.1rem;
    font-size: 12px;
    color: $primarycolorlighten10;

    /* Padding in Spectre comes from the <a> tag in menu items which
      we don't use. */
    .tile {
      padding: 0.6rem 0.8rem;
      margin: 0 -0.8rem;
      display: block;
    }

    strong {
      background-color: rgb(255,255,200);
    }
    &.selected {
      background-color: var(--primary-color-lighten-70-pale,
        $primarycolorlighten70pale) !important;
    }
  }

  button.clear {
    font-size: x-small;
    background-color: #AAA;
    color: #FFF;
    border: none;
    position: absolute;
    right: 6px;
    top: 3px;
    border-radius: 0.3em;
    height: 20px;
    cursor: pointer;
  }

  .clock {
    position: absolute;
    left: 7px;
    top: 10.5px;
    color: #c0c4cc;
  }

  .disabled-item {
    cursor: not-allowed;
    background-color: var(--secondary-color-lighten-55,
      $secondarycolorlighten55);
    color: #ccc;
  }
</style>
